import { useTranslation } from "react-i18next";
import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import apiRequest from "../../functions/api";
import { Card, Button, Typography, CardMedia, Grid, Box, CircularProgress, TextField, MenuItem, ListItemText, AlertTitle } from "@mui/material";
import MuiAlert from "@mui/material/Alert";
import { useMessage } from "../../context/message-context";
import Page from "../shared/components/PageComponent";
import Layout from "../shared/components/LayoutComponent";
import { DateTextField, FullPageSpinner, TimeTextField } from "../lib";
import ResponsiveDialog from "../shared/components/ResponsiveDialog";
import MessageBox from "../shared/components/MessageBox";
import { pad } from "../../functions/default";
import umlauteErsetzen from "../shared/functions/umlauteErsetzen";
import FileTransferHistory from "../TransferHistoryComponent/TransferHistory";
import { fetchFile, fetchMeldungHistorie, fetchBelegHistorie, fetchMeldungsarten, TransferHistory } from ".";
import openFile from "../shared/functions/download";
import { validateBoolean } from "../../functions/validateBoolean";
import { fetchMitarbeiterInfos } from "./fetchMitarbeiterInfos";
function Alert(props) {
  const { t } = useTranslation();
  return <MuiAlert elevation={2} variant="standard" {...props} />;
}
function ElectronicIllnessAttestation(props) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const { accessToken, client } = props;
  const [selectedFile, setSelectedFile] = useState(null);
  const [vonDatum, setVonDatum] = useState(new Date().getFullYear() + "-" + pad(new Date().getMonth() + 1, 2) + "-" + pad(new Date().getDate(), 2));

  const [bisDatum, setBisDatum] = useState(new Date().getFullYear() + "-" + pad(new Date().getMonth() + 1, 2) + "-" + pad(new Date().getDate(), 2));

  const [vonZeit, setVonZeit] = useState("00:00");
  const [bisZeit, setBisZeit] = useState("00:00");
  const [vonDatumError, setVonDatumError] = useState(null);
  const [vonZeitError, setVonZeitError] = useState(null);
  const [bisDatumError, setBisDatumError] = useState(null);
  const [bisZeitError, setBisZeitError] = useState(null);
  const [artDerKrankheitError, setArtDerKrankheitError] = useState(null);
  const [auGrund, setAuGrund] = useState("");
  const [defaultGrund, setDefaultGrund] = useState("");
  const [artDerKrankheit, setArtDerKrankheit] = useState("");
  const [bemerkung, setBemerkung] = useState("");
  const [bemerkungError, setBemerkungError] = useState("");
  const [auAb, setAuAb] = useState("");
  const [auAbLaut, setAuAbLaut] = useState("");
  const [zeitraum, setZeitraum] = useState(1);
  const [isSending, setIsSending] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [goBackDialogOpen, setGoBackDialogOpen] = useState(false);
  const [papierBescheinigung, setPapierBescheinigung] = useState(false);
  const navigate = useNavigate();
  const { showErrorMessage, showSuccessMessage } = useMessage();
  const [belegHistory, setBelegHistory] = useState({
    allKeysByDate: [],
    byKey: {},
  });
  const [meldungHistory, setMeldungHistory] = useState({
    allKeysByDate: [],
    byKey: {},
  });
  const [meldungsarten, setMeldungsarten] = useState([]);
  const [maInfos, setMaInfos] = useState({});

  const meldungFuerUserId = useMemo(() => {
    if (searchParams && searchParams.get("userId")) {
      return searchParams.get("userId");
    } else {
      return null;
    }
  }, [searchParams]);

  const program = useMemo(() => {
    if (location && location.pathname.startsWith("/SNDAUM")) {
      return "SNDAUM";
    } else {
      return "SENDAU";
    }
  }, [location]);

  const handleLoadMeldungHistory = () => {
    setMeldungHistory({
      allKeysByDate: [],
      byKey: {},
    });
    fetchMeldungHistorie(client, accessToken, program, meldungFuerUserId)
      .then((history) => {
        setMeldungHistory(history);
      })
      .catch((err) => {
        showErrorMessage(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  useEffect(() => {
    setIsLoading(true);

    const headers = {
      Authorization: "Bearer " + accessToken,
    };

    const payload = {
      mandant: client,
      program,
      statement: "IST_PAPIERBESCHEINIGUNG_NOTWENDIG",
      userId: meldungFuerUserId,
    };

    apiRequest("POST", "daten", payload, headers, initCb, errorCb);

    setBelegHistory({
      allKeysByDate: [],
      byKey: {},
    });
    fetchBelegHistorie(client, accessToken, program, meldungFuerUserId)
      .then((belegHistory) => {
        setBelegHistory(belegHistory);
      })
      .catch((err) => {
        showErrorMessage(err);
      })
      .finally(() => {
        setIsLoading(false);
      });

    handleLoadMeldungHistory();
    setMeldungsarten([]);
    fetchMeldungsarten(client, accessToken, program, meldungFuerUserId)
      .then((meldungsarten) => {
        setMeldungsarten(meldungsarten);
      })
      .catch((err) => {
        showErrorMessage(err);
      })
      .finally(() => {
        setIsLoading(false);
      });

    setMaInfos({});
    if (meldungFuerUserId && program === "SNDAUM") {
      fetchMitarbeiterInfos(client, accessToken, program, meldungFuerUserId)
        .then((infos) => {
          setMaInfos(infos);
        })
        .catch((err) => {
          showErrorMessage(err);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [meldungFuerUserId, program]);

  const initCb = (response) => {
    if (response.status === 200) {
      if (response.data.ist_papierbescheinigung_notwendig === "false") {
        setPapierBescheinigung(false);
      }
      if (validateBoolean(response.data.ist_papierbescheinigung_notwendig)) {
        setPapierBescheinigung(true);
        setAuGrund(response.data.grund.bez);
        setDefaultGrund(response.data.grund.bez);
      }
      setAuAb(response.data.au_ab);
      setAuAbLaut(response.data.au_ab_laut);
    }
    setIsLoading(false);
  };

  const errorCb = () => {
    setIsLoading(false);
    setIsSending(false);
  };

  useEffect(() => {
    const tmpVonDate = new Date(vonDatum);
    const tmpBisDate = new Date(bisDatum);
    let difference = tmpBisDate.getTime() - tmpVonDate.getTime();
    let totalDays = Math.ceil(difference / (1000 * 3600 * 24)) + 1;
    setZeitraum(totalDays);
  }, [vonDatum, bisDatum]);

  const dataCb = (response) => {
    if (response.status === 200) {
      setIsSending(false);
      if (response.data.message === "ok") {
        showSuccessMessage(t("Upload erfolgreich."));
        navigate(-1);
      }
    } else {
      setIsSending(false);
      if (response) {
        showErrorMessage(response.data.message);
      } else {
        showErrorMessage(t("Es ist etwas schief gelaufen. Bitte versuchen Sie es später erneut."));
      }
    }
  };

  const onSendHandler = () => {
    setBemerkungError("");
    if (artDerKrankheit === "") {
      setArtDerKrankheitError(t("Bitte geben Sie an, was Sie melden möchten."));
      return;
    }
    if (checkDates()) return;
    var fileName = "";
    if (vonDatumError) {
      if (artDerKrankheit === "03") {
        showErrorMessage(t("Bitte geben Sie den Tag des Arztbesuchs ein"));
      } else {
        showErrorMessage(t("Bitte geben Sie den Beginn der Krankheit ein"));
      }
      return;
    }
    if (bisDatumError) {
      showErrorMessage(t("Bitte geben Sie das Ende der Krankheit ein"));
      return;
    }

    if (bemerkungVerpflichtend(artDerKrankheit) && bemerkung === "") {
      setBemerkungError(t("Bitte geben Sie eine Bemerkung an."));
      return;
    }

    if (
      !selectedFile &&
      ((artDerKrankheit === "01" && papierBescheinigung) || artDerKrankheit === "03" || artDerKrankheit === "04" || artDerKrankheit === "05")
    ) {
      showErrorMessage(t("Bitte laden Sie einen Nachweis hoch."));
      return;
    }
    const data = new FormData();
    setIsSending(true);
    if (selectedFile) {
      fileName = umlauteErsetzen(selectedFile.name);
      data.append("file", selectedFile);
    }
    const headers = {
      Authorization: "Bearer " + accessToken,
    };
    const payload = {
      art: artDerKrankheit,
      bemerkung: bemerkung,
      vonDatum: vonDatum,
      bisDatum: bisDatum,
      vonZeit: vonZeit,
      bisZeit: bisZeit,
      mandant: client,
      program,
      userId: meldungFuerUserId,
      statement: "FEHLZEIT_MELDEN",
    };
    data.append("payload", JSON.stringify(payload));
    apiRequest("POST", "daten", data, headers, dataCb, errorCb);
  };

  const bemerkungZulassen = (art) => validateBoolean(meldungsarten.find((m) => m.key === art)?.bemerkungZulassen);
  const bemerkungVerpflichtend = (art) => validateBoolean(meldungsarten.find((m) => m.key === art)?.bemerkungVerpflichtend);

  const getDataUrl = (img) => {
    // Create canvas
    return window.URL.createObjectURL(img);
  };
  const onChangeHandler = (event) => {
    setSelectedFile(event.target.files[0]);
  };
  const closeInfoDialog = () => {
    setShowModal(false);
  };
  const changeVonDatum = (event) => {
    //checkVonDate(event.target.value);
    setVonDatum(event.target.value);
    if (artDerKrankheit === "03") setBisDatum(event.target.value);
  };

  const changeVonZeit = (event) => {
    setVonZeit(event.target.value);
  };

  const changeBisZeit = (event) => {
    setBisZeit(event.target.value);
  };
  const changeBisDatum = (event) => {
    //checkBisDate(event.target.value);
    setBisDatum(event.target.value);
  };
  const checkDates = () => {
    var error = false;
    if (vonDatum === "") {
      setVonDatumError(t("Bitte geben Sie ein Startdatum ein"));
      error = true;
      return error;
    }
    setVonDatumError(null);
    if (bisDatum === "") {
      setBisDatumError(t("Bitte geben Sie ein Enddatum ein"));
      error = true;
      return error;
    }
    if (bisDatum < vonDatum) {
      setBisDatumError(t("Das Enddatum darf nicht kleiner als das Startdatum sein"));
      error = true;
    } else {
      setBisDatumError(null);
    }

    if (artDerKrankheit === "03") {
      if (vonZeit === "00:00") {
        setVonZeitError(t("Bitte geben Sie die Von-Zeit ein"));
        error = true;
        return error;
      }
      setVonZeitError(null);

      if (bisZeit === "00:00") {
        setBisZeitError(t("Bitte geben Sie die Bis-Zeit ein"));
        error = true;
        return error;
      }

      if (bisZeit < vonZeit) {
        setBisZeitError(t("Die Bis-Zeit darf nicht kleiner sein als die Von_zeit ein"));
        error = true;
        return error;
      }
      setBisZeitError(null);
    }

    return error;
  };
  const openGoBackDialog = () => {
    setGoBackDialogOpen(true);
  };
  const closeGoBackDialog = () => {
    setGoBackDialogOpen(false);
  };
  const goBackDialogRespone = (response) => {
    if (response === "YES") {
      navigate("/");
    }
    closeGoBackDialog();
  };
  const handleArtDerKrankheitChange = (e) => {
    const value = e.target.value;
    setArtDerKrankheit(value);
    if (value === "04") {
      setAuGrund(t("Für die Auswahl 'Kind Krank' kann kein Nachweis per eAU abgerufen werden. Bitte laden Sie einen Nachweis hoch."));
    } else if (value === "05") {
      setAuGrund(t("Für die Auswahl 'Reha' kann kein Nachweis per eAU abgerufen werden. Bitte laden Sie einen Nachweis hoch."));
    } else if (value === "03") {
      setAuGrund(t("Für die Auswahl 'Arztbesuch ohne Krankmeldung' kann kein Nachweis per eAU abgerufen werden. Bitte laden Sie einen Nachweis hoch."));
    } else if (defaultGrund !== "") {
      setAuGrund(defaultGrund);
    } else {
      setAuGrund("");
    }
  };
  const handleBelegClicked = (beleg) => {
    setIsLoading(true);
    fetchFile(client, accessToken, beleg.key, beleg.betreff + "_" + beleg.key + beleg.dateiart, program, meldungFuerUserId)
      .then((file) => {
        openFile(file.name, file.content, file.type);
      })
      .catch((err) => {
        showErrorMessage(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const isVorgesetzenModus = useMemo(() => {
    return meldungFuerUserId && program === "SNDAUM" && maInfos && maInfos.pnr;
  }, [meldungFuerUserId, program, maInfos]);

  const handleBemerkungChange = (e) => {
    const value = e.target.value;
    setBemerkung(value);
  };
  if (isLoading) {
    return <FullPageSpinner />;
  }
  return (
    <Page>
      {!meldungsarten ||
        (meldungsarten.length == 0 && (
          <Layout>
            <Box p={1}>
              <Grid container justifyContent={"center"}>
                <Grid item xs={12}>
                  <Box p={1}>
                    <Typography align="left" variant="h4">
                      {meldungFuerUserId ? t("Mitarbeiter-Arbeitsunfähigkeit melden") : t("Arbeitsunfähigkeit melden")}
                    </Typography>
                  </Box>
                  <Box p={1}>
                    <Alert align="left" severity="warning">
                      <AlertTitle>
                        <strong>{t("Keine Meldungsarten zur Übermittlung freigeschalten")}</strong>
                      </AlertTitle>
                      {t("Ihre Personalabteilung hat keine Krankmeldungsarten für die Übermittlung zugelassen.")}
                    </Alert>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Layout>
        ))}
      {meldungsarten && meldungsarten.length > 0 && (
        <Layout>
          <Box p={1}>
            <Grid container justifyContent={"center"}>
              <Grid item xs={12}>
                <Box p={1}>
                  <Typography align="left" variant="h4">
                    {meldungFuerUserId ? t("Mitarbeiter-Arbeitsunfähigkeit melden") : t("Arbeitsunfähigkeit melden")}
                  </Typography>
                </Box>
                <Box p={1}>
                  <Typography align="left" variant="body2">
                    {t("Ab")} 01.01.2023 {t("werden AU-Bescheinigungen elektronisch bei der Krankenkasse angefragt.")} <br />
                    {t("Für die Anfrage benötigen wir die folgenden zusätzlichen Informationen von Ihnen:")}
                  </Typography>
                </Box>
              </Grid>
              {isVorgesetzenModus && (
                <Grid item xs={12}>
                  <Box p={1}>
                    <Typography align="left">{t("Für wen?")}</Typography>
                    <TextField value={`${maInfos.pnr} ${maInfos.vorname} ${maInfos.nachname}`} variant="outlined" fullWidth disabled={true} />
                  </Box>
                </Grid>
              )}
              <Grid item xs={12}>
                <Box p={1}>
                  <Typography align="left">{t("Was möchten Sie melden?")}</Typography>
                  <TextField
                    select
                    value={artDerKrankheit}
                    onChange={handleArtDerKrankheitChange}
                    variant="outlined"
                    helperText={artDerKrankheitError}
                    error={artDerKrankheitError !== null}
                    fullWidth
                    disabled={meldungsarten.length == 0}
                  >
                    {meldungsarten.map((art) => (
                      <MenuItem key={art.key} value={art.key}>
                        <ListItemText primary={art.bezeichnung} secondary={art.beschreibung} />
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Grid>
              {artDerKrankheit !== "02" || auAb !== "1" ? (
                <>
                  <Grid item xs={12}>
                    <Box p={1}>
                      <Typography align="left">
                        {isVorgesetzenModus && artDerKrankheit === "03" && t(`An welchem Tag war der Mitarbeitende beim Arzt?`)}
                        {!isVorgesetzenModus && artDerKrankheit === "03" && t("An welchem Tag waren Sie beim Arzt?")}

                        {isVorgesetzenModus && artDerKrankheit !== "03" && t(`Wann beginnt die Arbeitsunfähigkeit?`)}
                        {!isVorgesetzenModus && artDerKrankheit !== "03" && t("Wann beginnt Ihre Arbeitsunfähigkeit?")}
                      </Typography>
                      <DateTextField
                        value={vonDatum}
                        onChange={changeVonDatum}
                        variant="outlined"
                        helperText={vonDatumError}
                        error={vonDatumError !== null}
                        fullWidth
                      ></DateTextField>
                    </Box>
                  </Grid>
                  {artDerKrankheit !== "03" && (
                    <Grid item xs={12}>
                      <Box p={1}>
                        <Typography align="left">
                          {isVorgesetzenModus && t(`Wann endet die Arbeitsunfähigkeit?`)}
                          {!isVorgesetzenModus && t("Wann endet Ihre Arbeitsunfähigkeit?")}
                        </Typography>
                        <DateTextField
                          value={bisDatum}
                          onChange={changeBisDatum}
                          variant="outlined"
                          helperText={bisDatumError}
                          error={bisDatumError !== null}
                          fullWidth
                        ></DateTextField>
                      </Box>
                    </Grid>
                  )}
                  {artDerKrankheit === "03" && (
                    <>
                      <Grid item xs={12} md={6}>
                        <Box p={1}>
                          <Typography align="left">{t("Ab wann waren Sie beim Arzt?")}</Typography>
                          <TimeTextField
                            value={vonZeit}
                            onChange={changeVonZeit}
                            variant="outlined"
                            helperText={vonZeitError}
                            error={vonZeitError !== null}
                            fullWidth
                          ></TimeTextField>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Box p={1}>
                          <Typography align="left">{t("Bis wann waren Sie beim Arzt?")}</Typography>
                          <TimeTextField
                            value={bisZeit}
                            onChange={changeBisZeit}
                            variant="outlined"
                            helperText={bisZeitError}
                            error={bisZeitError !== null}
                            fullWidth
                          ></TimeTextField>
                        </Box>
                      </Grid>
                    </>
                  )}
                  <Grid item xs={6}></Grid>
                  {(artDerKrankheit === "02" || bemerkungZulassen(artDerKrankheit) || bemerkungVerpflichtend(artDerKrankheit)) && (
                    <Grid item xs={12}>
                      <Box p={1}>
                        <Typography align="left">{t("Bemerkung")}</Typography>
                        <TextField
                          value={bemerkung}
                          onChange={handleBemerkungChange}
                          variant="outlined"
                          fullWidth
                          helperText={bemerkungError}
                          error={!!bemerkungError}
                        ></TextField>
                      </Box>
                    </Grid>
                  )}
                  {["01", "03", "04", "05"].includes(artDerKrankheit) && (
                    <Grid item xs={12}>
                      {auGrund && (
                        <Box p={1}>
                          <Alert severity="warning">{auGrund}</Alert>
                        </Box>
                      )}
                      {!papierBescheinigung && artDerKrankheit === "01" && (
                        <Box p={1}>
                          <Alert severity="success">
                            {isVorgesetzenModus &&
                              t(
                                `Die AU-Bescheinigung wird elektronisch von der Krankenkasse angefragt. Wenn Sie einen Beleg erhalten haben, können Sie diesen zusätzlich als Nachweis hochladen.`,
                              )}
                            {!isVorgesetzenModus &&
                              t(
                                "Ihre AU-Bescheinigung wird elektronisch von der Krankenkasse angefragt. Wenn Sie von Ihrem Arzt einen Beleg erhalten haben, können Sie diesen zusätzlich als Nachweis hochladen.",
                              )}
                          </Alert>
                        </Box>
                      )}
                      <form>
                        <div>
                          <input color="primary" type="file" id="file-input" onChange={onChangeHandler} style={{ display: "none" }} />
                          <label htmlFor="file-input" className="mr-2">
                            <Button id="au_upload" variant="contained" component="span" color="primary" label={t("Dateiupload")}>
                              {!papierBescheinigung && artDerKrankheit === "01" ? t("Nachweis hochladen (optional)") : t("Nachweis hochladen")}
                            </Button>
                          </label>
                        </div>
                      </form>
                    </Grid>
                  )}
                </>
              ) : (
                <Grid item xs={12}>
                  <Box p={1}>
                    <Alert severity="error">
                      {t(
                        "Krankmeldung ohne Arztbesuch nicht zulässig. Sie müssen laut " +
                          auAbLaut +
                          " ab Tag " +
                          auAb +
                          " einen Nachweis erbringen. Bitte lassen Sie sich von einem Arzt krankschreiben und melden Sie „Krankmeldung mit Arztbesuch“.",
                      )}
                    </Alert>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>
          {selectedFile && (
            <Grid container justifyContent="center">
              <Grid item xs={12} sm={10} md={8}>
                <Box p={2}>
                  <Card>
                    <CardMedia component="img" alt="AU-Bescheinigung" image={getDataUrl(selectedFile)} title={t("AU-Bescheinigung")} />
                  </Card>
                  <Box p={2}>
                    <Typography color="textSecondary">{selectedFile.name}</Typography>
                  </Box>
                </Box>
                <Box p={2} pt={0}>
                  <Alert severity="warning">
                    {t("ACHTUNG: Bitte nur die Ausfertigung für den Arbeitgeber übermitteln.")}
                    <br />
                    {t("Durch das Betätigen des Senden Buttons wird die ausgewählte AU-Bescheinigung an Ihre Personalabteilung übermittelt!")}
                  </Alert>
                </Box>
              </Grid>
            </Grid>
          )}
          {Number(auAb) <= Number(zeitraum) && artDerKrankheit === "02" && auAb !== "1" && (
            <Grid container justifyContent="center">
              <Grid item xs={12} md={10} lg={8}>
                <Box p={2} pt={0}>
                  <Alert severity="error">
                    {t(
                      "Krankmeldung ohne Arztbesuch für diesen Zeitraum nicht zulässig. Der eingegebene Zeitraum beträgt " +
                        zeitraum +
                        " Tage. Sie müssen laut " +
                        auAbLaut +
                        " aber ab Tag " +
                        auAb +
                        " einen Nachweis erbringen.",
                    )}
                    <br />
                    {t("Bitte lassen Sie sich von einem Arzt krankschreiben und melden Sie „Krankmeldung mit Arztbesuch“.")}
                  </Alert>
                </Box>
              </Grid>
            </Grid>
          )}
          <Grid container justifyContent="flex-end">
            <Grid item>
              <Box p={2}>
                <Grid container>
                  <Grid item>
                    <Box pr={1}>
                      <Button id="back" color="primary" onClick={openGoBackDialog} fullWidth>
                        {t("Abbruch")}
                      </Button>
                    </Box>
                  </Grid>
                  <Grid item>
                    <Box pl={1}>
                      <Button
                        id="senden"
                        variant="contained"
                        disabled={isSending || (Number(auAb) <= zeitraum && artDerKrankheit === "02") || (auAb === "1" && artDerKrankheit === "02")}
                        component="span"
                        color="primary"
                        onClick={onSendHandler}
                        fullWidth
                        endIcon={isSending && <CircularProgress color="inherit" />}
                      >
                        {t("Senden")}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Grid>
        </Layout>
      )}
      <Layout>
        <Box p={1}>
          <TransferHistory history={meldungHistory} hideSearchbar={true} reloadHistory={handleLoadMeldungHistory} program={program} />
        </Box>
      </Layout>
      <Layout>
        <Box p={1}>
          <FileTransferHistory history={belegHistory} onBelegClicked={handleBelegClicked} hideSearchbar={true} />
        </Box>
      </Layout>
      <ResponsiveDialog
        open={showModal}
        setOpen={closeInfoDialog}
        title={t("Muss ich ein Foto der AU-Bescheinigung hochladen?")}
        actionButton={
          <Button variant="contained" color="primary" onClick={closeInfoDialog}>
            {t("Ok")}
          </Button>
        }
      >
        <Box p={2}>
          <Grid container>
            <Grid item xs={12}>
              <Typography>{t("In folgenden Fällen müssen Sie keine Arbeitsunfähigkeitsbescheinigung senden:")}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>- {t("Arbeitsunfähigkeit wegen Krankheit Vertragsarzt / Vertragszahnarzt (§ 295 Abs. 1 Satz 1 SGB V)")}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>- {t("Arbeitsunfähigkeit wegen Arbeitsunfall und Berufskrankheiten (§ 201 Abs. 2 SGB VII)")}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>- {t("Arbeitsunfähigkeit bei stationärer Krankenhausbehandlung Krankenkasse (§ 301 Abs. 1 SGB V)")}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>{t("In diesem Fall bekommt ectool die Bescheinigung elektronisch von der Krankenkasse.")}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography>{t("Bei allen anderen Krankheiten muss eine Arbeitsunfähigkeitsbescheinigung mitgeschickt werden.")}</Typography>
            </Grid>
          </Grid>
        </Box>
      </ResponsiveDialog>
      <MessageBox
        open={goBackDialogOpen}
        onClose={closeGoBackDialog}
        title={t("Achtung")}
        text={t("Ihre Änderungen werden verworfen. Wollen Sie die Seite verlassen?")}
        handleResponse={goBackDialogRespone}
        buttons="YESNO"
      />
      {isLoading && <FullPageSpinner />}
    </Page>
  );
}
export default ElectronicIllnessAttestation;
